import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
import useFormState from 'hooks/useFormState';
import keyGenerator from 'utils/keys';
import { constants as loadConstants } from 'utils/loads';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectors as orderSelectors } from 'state/order';
import {
  actions as loadsActions,
  selectors as loadSelectors,
} from 'state/loads';
// styles
import styled from 'styled-components';
// components
import { Field } from '@ontruck/orc';
import FieldError from 'components/form/FieldError';

const LoadSizeInput = ({
  className,
  shipmentKey,
  loadKey,
  field,
  customMax = null,
  canBePredefined = false,
  growLeft = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation('LoadInputs');
  const dispatch = useDispatch();

  const stopId = `${shipmentKey}-${loadKey}`;

  const fieldId = useMemo(
    () =>
      keyGenerator.load({
        shipmentKey,
        loadKey,
        field,
      }),
    [shipmentKey, loadKey, field]
  );

  const unit = loadConstants.FIELDS_CONFIG[field].UNIT;
  const min = loadConstants.FIELDS_CONFIG[field].MIN;
  const max = customMax || loadConstants.FIELDS_CONFIG[field].MAX;

  const maxItemsInLoad = useSelector((state) =>
    orderSelectors.getMaxItemsInThisLoad(state, shipmentKey, loadKey)
  );

  const value = useSelector(
    (state) => loadSelectors.getLoad(state, shipmentKey, loadKey)[field]
  );

  const displayValue = !!value ? value : '';

  const label =
    displayValue !== '' || isFocused
      ? `${t(`${field}.label`)}`
      : `${t(`${field}.label`)} (${unit})`;

  const hasValidationError = !!useSelector((state) =>
    loadSelectors.getLoadError(state, shipmentKey, loadKey, field)
  );

  const { fieldHasError } = useFormState(stopId, field, value);
  const errorClassName =
    !!fieldHasError || hasValidationError ? 'error' : 'not-error';

  const palletType = useSelector((state) =>
    loadSelectors.getPalletType(state, shipmentKey, loadKey)
  );
  const load = useSelector((state) =>
    loadSelectors.getLoad(state, shipmentKey, loadKey)
  );

  const {
    item_height: parcelHeight,
    item_length: parcelLength,
    item_width: parcelWidth,
  } = loadConstants.NON_PALLET_SIZES.PARCEL_STANDART;

  const isParcel =
    load?.load_type === loadConstants.LOAD_TYPES.PARCEL &&
    field !== loadConstants.FIELDS.TOTAL_WEIGHT;

  const selectedLoadType = useSelector((state) =>
    loadSelectors.getLoadType(state, shipmentKey, loadKey)
  );

  const isPallet = selectedLoadType === loadConstants.LOAD_TYPES.PALLET;

  const isPalletPredefined =
    isPallet && palletType !== loadConstants.PALLET_TYPES.CUSTOM_PALLET;

  const isParcelPredifined =
    isParcel &&
    load.item_height === parcelHeight &&
    load.item_length === parcelLength &&
    load.item_width === parcelWidth;

  const isPredefined = isPalletPredefined || isParcelPredifined;

  const predefinedClassName =
    canBePredefined && isPredefined ? 'predefined' : '';

  const showErrorTooltip = hasValidationError && isFocused;

  const handleOnFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleOnBlur = useCallback(() => {
    setIsFocused(false);
  }, [setIsFocused]);

  const handleOnChange = useCallback(
    (value) => {
      dispatch(
        loadsActions.changeLoadValue({
          shipmentKey,
          loadKey,
          field,
          maxItemsInLoad,
          value,
        })
      );
    },
    [dispatch, shipmentKey, loadKey, field, maxItemsInLoad]
  );

  return (
    <div className={className}>
      {showErrorTooltip && (
        <div>
          <FieldError growLeft={growLeft}>
            {t(`${field}.errorMsg`, { max, min, unit })}
          </FieldError>
        </div>
      )}
      <Field
        type="text"
        id={fieldId}
        className={`${errorClassName} ${predefinedClassName}`}
        name={fieldId}
        label={label}
        asideRightText={unit}
        value={displayValue}
        onChange={(_, { value }) => handleOnChange(value)}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        size="L"
        fullWidth
      />
    </div>
  );
};

LoadSizeInput.propTypes = {
  className: PropTypes.string,
  shipmentKey: PropTypes.string.isRequired,
  loadKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  field: PropTypes.string.isRequired,
  customMax: PropTypes.number,
  canBePredefined: PropTypes.bool,
  growLeft: PropTypes.bool,
};

export default styled(LoadSizeInput)`
  position: relative;
`;
